import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { Save, Globe, Image } from 'lucide-react';
import PageHeader from '../../components/admin/PageHeader';
import ContentCard from '../../components/admin/ContentCard';
import LoadingState from '../../components/admin/LoadingState';
import toast from 'react-hot-toast';

const PartnerForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditing = !!id;
  
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [partner, setPartner] = useState({
    name: '',
    logo: '',
    website: ''
  });

  useEffect(() => {
    document.title = isEditing ? 'Modifier un Partenaire | Admin' : 'Ajouter un Partenaire | Admin';
    
    if (isEditing) {
      loadPartner();
    }
  }, [isEditing, id]);

  const loadPartner = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('partners')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;
      
      if (data) {
        setPartner(data);
      }
    } catch (error) {
      console.error('Error loading partner:', error);
      toast.error('Erreur lors du chargement du partenaire');
      navigate('/admin/partners');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setPartner({ ...partner, [name]: value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setSaving(true);
      
      // Validate required fields
      if (!partner.name) {
        toast.error('Veuillez renseigner le nom du partenaire');
        return;
      }
      
      let response;
      
      if (isEditing) {
        // Update existing partner
        response = await supabase
          .from('partners')
          .update({
            name: partner.name,
            logo: partner.logo,
            website: partner.website
          })
          .eq('id', id);
      } else {
        // Insert new partner
        response = await supabase
          .from('partners')
          .insert([{
            name: partner.name,
            logo: partner.logo,
            website: partner.website
          }]);
      }
      
      if (response.error) throw response.error;
      
      toast.success(isEditing ? 'Partenaire mis à jour avec succès' : 'Partenaire ajouté avec succès');
      navigate('/admin/partners');
    } catch (error) {
      console.error('Error saving partner:', error);
      toast.error('Erreur lors de l\'enregistrement du partenaire');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="px-6">
        <PageHeader
          title={isEditing ? 'Modifier le Partenaire' : 'Ajouter un Partenaire'}
          backLink="/admin/partners"
        />
        <LoadingState type="form" />
      </div>
    );
  }

  return (
    <div className="px-6">
      <PageHeader
        title={isEditing ? 'Modifier le Partenaire' : 'Ajouter un Partenaire'}
        backLink="/admin/partners"
      />
      
      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          <div className="lg:col-span-2">
            <ContentCard title="Informations du Partenaire">
              <div className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                    Nom du partenaire *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={partner.name}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    required
                  />
                </div>
                
                <div>
                  <label htmlFor="website" className="block text-sm font-medium text-gray-700 mb-1">
                    Site web
                  </label>
                  <div className="relative">
                    <Globe size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                      type="url"
                      id="website"
                      name="website"
                      value={partner.website}
                      onChange={handleChange}
                      className="pl-10 pr-3 py-2 w-full border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      placeholder="https://example.com"
                    />
                  </div>
                </div>
                
                <div>
                  <label htmlFor="logo" className="block text-sm font-medium text-gray-700 mb-1">
                    Logo (URL)
                  </label>
                  <div className="relative">
                    <Image size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                      type="url"
                      id="logo"
                      name="logo"
                      value={partner.logo}
                      onChange={handleChange}
                      className="pl-10 pr-3 py-2 w-full border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      placeholder="https://example.com/logo.png"
                    />
                  </div>
                </div>
              </div>
            </ContentCard>
          </div>
          
          <div>
            <ContentCard title="Aperçu">
              <div className="space-y-6">
                {partner.logo ? (
                  <div className="flex justify-center">
                    <img
                      src={partner.logo}
                      alt={partner.name}
                      className="max-w-full h-auto max-h-40 object-contain"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = 'https://via.placeholder.com/200x100?text=Logo+non+disponible';
                      }}
                    />
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-6 text-gray-400 border border-dashed border-gray-300 rounded-md">
                    <Image size={32} className="mb-2" />
                    <p className="text-sm">Aucun logo</p>
                  </div>
                )}
                
                <div className="p-4 bg-gray-50 rounded-md">
                  <h3 className="font-medium text-gray-900 mb-2">{partner.name || 'Nom du partenaire'}</h3>
                  {partner.website ? (
                    <a 
                      href={partner.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary-600 hover:text-primary-700 flex items-center text-sm"
                    >
                      <Globe size={14} className="mr-1" />
                      {partner.website.replace(/^https?:\/\/(www\.)?/, '')}
                    </a>
                  ) : (
                    <p className="text-sm text-gray-500">Aucun site web</p>
                  )}
                </div>
              </div>
            </ContentCard>
          </div>
        </div>
        
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={() => navigate('/admin/partners')}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={saving}
            className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors disabled:opacity-50 flex items-center"
          >
            {saving ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Enregistrement...
              </>
            ) : (
              <>
                <Save size={16} className="mr-2" />
                {isEditing ? 'Mettre à jour' : 'Enregistrer'}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default PartnerForm;